import { useEffect, useState } from "react";
import AgendaVista from "../vista/agendaVista"
import type { DateLocalizer, SlotInfo, View } from "react-big-calendar";
import type { ITarea } from "../../../modelos/Tarea/entidades/ITarea";
import { estadoInicialFiltroTarea, estadoInicialTarea } from "../../Tareas/interfaces";
import type { IEventoTarea, IFiltroAgenda } from "../interfaces";
import { TareaModelo } from "../../../modelos/Tarea";
import type { IUsuario } from "../../../modelos/Usuario/entidades/IUsuario";
import ModeloUsuario from "../../../modelos/Usuario";
import { EnumPerfiles } from "../../../globales/enums/enumPerfiles";
import { EnumTipoNotificacion } from "../../../globales/interfaces";
import { useNotificaciones } from "../../../globales/contextos/notificationContext";
import { useCargando } from "../../../globales/contextos/cargandoContext";
import { estadoInicialFiltroUsuario } from "../../usuarios/interfaces";
import type { ITareaFormulario } from "../../Tareas/interfaces/ITareaFormulario";
import dayjs from "dayjs";
import { EnumEstadoTarea } from "../../Tareas/enums/enumEstadoTarea";
import { ModuloTareaModelo } from "../../../modelos/ModulosTarea";
import { ClienteModelo } from "../../../modelos/Clientes";
import { ProyectoModelo } from "../../../modelos/Proyectos";
import { TipoTareaModelo } from "../../../modelos/TipoTarea";
import { ModuloModelo } from "../../../modelos/ModulosCheckList";
import { EstadoTareaModelo } from "../../../modelos/EstadoTarea";
import { UbicacionModelo } from "../../../modelos/Ubicacion";
import ModeloCausalEmergencia from "../../../modelos/CausalEmergencia";
import { estadoInicialFiltroCliente } from "../../clientes/interfaces";
import { estadoInicialFiltroProyecto } from "../../proyectos/interfaces";
import type { IEntidadesRegistroTarea } from "../../Tareas/interfaces";
import type { IRespuestaCargaMasiva } from "../../../modelos/Tarea/entidades/IRespuestaCargaMasiva";
import { useAutenticacion } from "../../../globales/contextos/authContext";
import { codigosVisitaInstalacionConsulta } from "../../Tareas/utils/permisosTareaUtils";

const AgendaCV = () => {
    const { usuario } = useAutenticacion();
    // Mismas actividades de Visita Instalación que en Tareas (Analista: solo Verificación).
    const codigosVisitaInstalacion = codigosVisitaInstalacionConsulta(usuario?.Permisos?.map(p => p.Codigo) ?? []);
    const modeloTarea = new TareaModelo();
    const modeloUsuario = new ModeloUsuario();
    const modeloModulosTarea = new ModuloTareaModelo();
    const modeloCliente = new ClienteModelo();
    const modeloProyecto = new ProyectoModelo();
    const modeloTipoTarea = new TipoTareaModelo();
    const modeloModulo = new ModuloModelo();
    const modeloEstadoTarea = new EstadoTareaModelo();
    const modeloUbicacion = new UbicacionModelo();
    const modeloCausalEmergencia = new ModeloCausalEmergencia();

    const { mostrarNotificacion } = useNotificaciones();
    const { manejarCargando } = useCargando();

    const [filtrosAbiertos, setFiltrosAbiertos] = useState(true);
    const [fecha, setFecha] = useState<Date>(new Date());
    const [vista, setVista] = useState<View>("month");
    const [eventosTareas, setEventosTareas] = useState<IEventoTarea[]>([]);
    const [listaTecnicos, setListaTecnicos] = useState<IUsuario[]>([]);
    const [abrirModal, setAbrirModal] = useState(false);
    const [pantallaCompleta, setPantallaCompleta] = useState(false);
    const [entidadTarea, setEntidadTarea] = useState<ITareaFormulario>(estadoInicialTarea)

    // Estado del filtro que viene de filtrosAgendaCV
    const [filtroAgenda, setFiltroAgenda] = useState<IFiltroAgenda>({
        Fecha: new Date(),
        Tecnicos: []
    });

    const [visualizacion, setVisualizacion] = useState<boolean>(false);
    const [abrirModalErrores, setAbrirModalErrores] = useState(false);
    const [datosRespuestaCarga, setDatosRespuestaCarga] = useState<IRespuestaCargaMasiva>({
        TareasGuardadas: 0,
        TareasRechazadas: 0,
        Rechazadas: []
    });

    const [entidadesRegistro, setEntidadesRegistro] = useState<IEntidadesRegistroTarea>({
        Proyectos: [],
        Clientes: [],
        Tecnicos: [],
        TecnicosTodos: [],
        Equipos: [],
        TiposTarea: [],
        ModulosCheckList: [],
        ModulosCheckListTodos: [],
        CausalesEmergencia: [],
        ClientesTodos: [],
        EquiposTodos: [],
        EstadosTarea: [],
        ProyectosTodos: [],
        Paises: [],
        HabilitacionesNBS: []
    });

    const tecnicosParaAgenda = filtroAgenda.Tecnicos.length > 0
        ? filtroAgenda.Tecnicos
        : listaTecnicos;

    // Sincroniza la fecha del filtro con la fecha usada por el Calendar
    // Normalizamos con dayjs a inicio de día en zona local para evitar
    // desplazamientos por zona horaria al parsear ISO/string.
    useEffect(() => {
        if (filtroAgenda.Fecha) {
            // Convertimos a Date asegurando que no haya desfase de horas
            const nuevaFecha = dayjs(filtroAgenda.Fecha).toDate();
            setFecha(nuevaFecha);
        }
    }, [filtroAgenda.Fecha]);

    useEffect(() => {
        const ejecutarConsulta = async () => {
            const { inicio, fin } = calcularRangoFechas(filtroAgenda.Fecha, vista);
            await consultarTareas(inicio, fin, filtroAgenda.Tecnicos);
        };
        ejecutarConsulta();
    }, [filtroAgenda.Fecha, vista, filtroAgenda.Tecnicos]);


    // Cargar técnicos y catálogos al inicio
    useEffect(() => {
        consultarEntidadesRegistro();
    }, []);

    const consultarEntidadesRegistro = async () => {
        try {
            manejarCargando(true, undefined, true);
            const [clientes, proyectos, tiposTarea, tecnicos, estadosTarea, paises, modulos, causalesEmergencia] = await Promise.all([
                modeloCliente.obtenerListaClientes(estadoInicialFiltroCliente),
                modeloProyecto.obtenerListaProyectos(estadoInicialFiltroProyecto.NitProyecto),
                modeloTipoTarea.obtenerListaTiposTarea(),
                modeloUsuario.obtenerListaUsuarios(estadoInicialFiltroUsuario),
                modeloEstadoTarea.obtenerListaEstadosTarea(),
                modeloUbicacion.obtenerPaises(),
                modeloModulo.obtenerListaModulos(),
                modeloCausalEmergencia.obtenerListaCausalesEmergencia()
            ]);

            const listaTecnicos = tecnicos.Data.filter(u => u.PerfilUsuarioId === EnumPerfiles.Tecnico);
            setListaTecnicos(listaTecnicos);

            setEntidadesRegistro({
                Proyectos: proyectos.Data.filter(p => p.Activo && p.Eliminado === false),
                ClientesTodos: clientes.Data,
                EquiposTodos: [],
                ProyectosTodos: proyectos.Data.filter(p => p.Activo && p.Eliminado === false),
                Clientes: clientes.Data,
                Tecnicos: listaTecnicos,
                TecnicosTodos: listaTecnicos,
                Equipos: [],
                TiposTarea: tiposTarea.Data,
                ModulosCheckList: modulos.Data.Modulos,
                ModulosCheckListTodos: modulos.Data.Modulos,
                EstadosTarea: estadosTarea.Data,
                CausalesEmergencia: causalesEmergencia.Data,
                Paises: paises.Data,
                HabilitacionesNBS: []
            });
        } catch {
            mostrarNotificacion(
                EnumTipoNotificacion.Error,
                'Error al cargar catálogos',
                'No fue posible cargar algunos datos necesarios para el formulario de tareas.',
                5000
            );
        } finally {
            manejarCargando(false);
        }
    }

    // Consultar tareas cuando cambie fecha, vista o filtro
    // useEffect(() => {
    //     // if (listaTecnicos.length === 0) return;

    //     const fechaBase = filtroAgenda.Fecha instanceof Date ? filtroAgenda.Fecha : dayjs(filtroAgenda.Fecha).toDate();
    //     const { inicio, fin } = calcularRangoFechas(fechaBase, vista);
    //     consultarTareas(inicio, fin, filtroAgenda.Tecnicos);
    // }, [filtroAgenda, vista]);

    const consultarTareas = async (inicio: Date, fin: Date, tecnicos: IUsuario[]) => {
        try {
            manejarCargando(true);

            const filtro = {
                ...estadoInicialFiltroTarea,
                FechaInicio: dayjs(inicio).format("YYYY-MM-DDTHH:mm:ss"),
                FechaFin: dayjs(fin).format("YYYY-MM-DDTHH:mm:ss"),
                CodigosVisitaInstalacion: codigosVisitaInstalacion,
            };

            const servicios = await modeloTarea.obtenerListaTareas(filtro);

            // Filtrar por técnicos seleccionados
            let tareasFiltradas = servicios.Data;
            if (tecnicos.length > 0) {
                const idsSeleccionados = tecnicos.map(t => t.Id);
                tareasFiltradas = servicios.Data.filter((s: ITarea) =>
                    idsSeleccionados.includes(s.AsignadaA)
                );
            }

            setEventosTareas(
                tareasFiltradas.map((servicio: ITarea) => {
                    return {
                        ClienteId: servicio.Equipo?.Proyecto?.Cliente?.Id || 0,
                        NumeroContrato: servicio.Equipo?.Proyecto?.Contrato?.Numero || '',
                        CodigoEquipo: servicio.Equipo?.Codigo || '',
                        Duracion: 0,
                        Id: servicio.Id || 0,
                        ProyectoId: servicio.ProyectoId,
                        EquipoId: servicio.EquipoId,
                        TipoServicioId: servicio.TipoServicioId,
                        EstadoTareaId: servicio.EstadoTareaId,
                        EstadoId: servicio.EstadoId,
                        FechaInicio: servicio.FechaInicio,
                        FechaFin: servicio.FechaFin,
                        EsEmergencia: servicio.EsEmergencia,
                        CreadaPor: servicio.CreadaPor,
                        AsignadaA: servicio.AsignadaA,
                        CausalEmergenciaId: servicio.CausalEmergenciaId,
                        UsuarioAsignado: servicio.UsuarioAsignado,
                        ModulosChecklist: servicio.ModulosChecklist,
                        CreadaUtc: servicio.CreadaUtc,
                        Equipo: servicio.Equipo,
                        EstadoServicio: servicio.EstadoServicio,
                        TipoTarea: servicio.TipoTarea,
                        CausalEmergencia: servicio.CausalEmergencia,
                        title: '',
                        resource: servicio.UsuarioAsignado?.NombreCompleto || '',
                        start: convertirSinZona(servicio.FechaInicio),
                        end: convertirSinZona(servicio.FechaFin),
                        LLegadaPunto: servicio.LLegadaPunto,
                        FinalizacionPunto: servicio.FinalizacionPunto,
                    }
                })
            );
        } catch (error) {
            console.error("Error al consultar tareas:", error);
            mostrarNotificacion(
                EnumTipoNotificacion.Error,
                "Error al consultar",
                "No se pudieron cargar las tareas.",
                5000
            );
        } finally {
            manejarCargando(false);
        }
    };

    const convertirSinZona = (fechaIso: string): Date => {
        const limpia = fechaIso.replace("Z", "");
        return new Date(limpia);
    };

    const manejarSeleccionEspacio = (infoSeleccion: SlotInfo) => {
        const ahora = new Date();

        // 1. Validar que la fecha de inicio no sea menor a la actual
        // Comparamos los objetos Date directamente
        if (infoSeleccion.start < ahora) {
            mostrarNotificacion(
                EnumTipoNotificacion.Information,
                "Acción no permitida",
                "No puedes programar tareas en fechas u horas pasadas.",
                4000
            );
            return; // Detiene la ejecución y no abre el modal
        }

        // 2. Calcular la duración dinámicamente
        // La diferencia en milisegundos dividida por (1000 * 60) da los minutos
        const diferenciaMs = infoSeleccion.end.getTime() - infoSeleccion.start.getTime();
        const duracionMin = diferenciaMs / (1000 * 60);

        // 3. Abrir el modal con los datos calculados
        abrirModalTarea(true, {
            ...entidadTarea,
            FechaInicio: infoSeleccion.start.toISOString(),
            FechaFin: infoSeleccion.end.toISOString(),
            AsignadaA: Number(infoSeleccion.resourceId),
            Duracion: duracionMin // Se asigna la duración calculada del arrastre
        });
    };

    const manejarSeleccionEvento = (evento: IEventoTarea) => {
        abrirModalTarea(true, mapearTarea(evento), (evento.EstadoServicio?.Id === EnumEstadoTarea.Exitoso ||
            evento.EstadoServicio?.Id === EnumEstadoTarea.EnCurso ||
            evento.EstadoServicio?.Id === EnumEstadoTarea.Cancelado ||
            evento.EstadoServicio?.Id === EnumEstadoTarea.Fallido ||
            evento.EstadoServicio?.Id === EnumEstadoTarea.PendienteFirma) ? true : false)
    };

    const mapearTarea = (eventoTarea: IEventoTarea) => {
        const servicio: ITareaFormulario = {
            Id: eventoTarea.Id,
            ProyectoId: eventoTarea.ProyectoId,
            EquipoId: eventoTarea.EquipoId,
            TipoServicioId: eventoTarea.TipoServicioId,
            EstadoId: eventoTarea.EstadoId,
            EsEmergencia: eventoTarea.EsEmergencia,
            CreadaPor: eventoTarea.CreadaPor,
            AsignadaA: eventoTarea.AsignadaA,
            CausalEmergenciaId: eventoTarea.CausalEmergenciaId,
            ModulosChecklist: eventoTarea.ModulosChecklist,
            CreadaUtc: eventoTarea.CreadaUtc,
            ClienteId: eventoTarea.Equipo?.Proyecto?.ClienteId || 0,
            NumeroContrato: eventoTarea.Equipo?.Proyecto?.Contrato?.Numero || '',
            CodigoEquipo: eventoTarea.Equipo?.Codigo || '',
            CausalEmergencia: eventoTarea.CausalEmergencia,
            TipoTarea: eventoTarea.TipoTarea,
            EstadoServicio: eventoTarea.EstadoServicio,
            Equipo: eventoTarea.Equipo,
            UsuarioAsignado: eventoTarea.UsuarioAsignado,
            Duracion: (new Date(eventoTarea.end!).getTime() - new Date(eventoTarea.start!).getTime()) / (1000 * 60),
            FechaInicio: eventoTarea.start!.toISOString(),
            FechaFin: eventoTarea.end!.toISOString(),
            LLegadaPunto: eventoTarea.LLegadaPunto,
            FinalizacionPunto: eventoTarea.FinalizacionPunto,
        }
        return servicio;
    }

    const formatosCalendario = {
        weekdayFormat: (date: Date, culture: string | undefined, localizer: DateLocalizer | undefined): string => {
            const text = localizer!.format(date, "eeee", culture);
            return text.charAt(0).toUpperCase() + text.slice(1);
        },

        monthHeaderFormat: (date: Date, culture: string | undefined, localizer: DateLocalizer | undefined): string => {
            const text = localizer!.format(date, "MMMM yyyy", culture);
            return text.charAt(0).toUpperCase() + text.slice(1);
        },

        dayRangeHeaderFormat: (
            { start, end }: { start: Date; end: Date },
            culture: string | undefined,
            localizer: DateLocalizer | undefined
        ): string => {
            const startStr = localizer!.format(start, "d MMM yyyy", culture);
            const endStr = localizer!.format(end, "d MMM yyyy", culture);
            return `${startStr} — ${endStr}`;
        },

        agendaDateFormat: (date: Date, culture: string | undefined, localizer: DateLocalizer | undefined): string => {
            const text = localizer!.format(date, "eeee, d MMM", culture);
            return text.charAt(0).toUpperCase() + text.slice(1);
        },
    };

    const manejarCambioFecha = (fechaNueva: Date) => {
        // Actualizamos el filtro, lo que disparará el useEffect de consulta
        setFiltroAgenda(prev => ({
            ...prev,
            Fecha: dayjs(fechaNueva).startOf('day').toDate()
        }));
    };

    const manejarCambioVista = (vistaNueva: View) => {
        setVista(vistaNueva)
        try {
            // Si cambiamos a vista 'day', actualizar sólo la fecha del calendario
            // para que muestre la fecha seleccionada en el filtro, pero no
            // sobrescribimos el filtro aquí (evita bucles y mantiene la
            // regla: el calendario puede modificar el filtro al navegar,
            // y el filtro modifica el calendario al seleccionar una fecha).
            if (vistaNueva === "day") {
                const f = filtroAgenda?.Fecha ? new Date(String(filtroAgenda.Fecha)) : new Date();
                if (!isNaN(f.getTime()) && f.getTime() !== fecha.getTime()) {
                    setFecha(f);
                }
            }
        } catch (e) {
            console.warn("Error al sincronizar vista/day con filtro de fecha", e);
        }
    }

    const manejarHabilitarCambioFiltros = () => {
        setFiltrosAbiertos(!filtrosAbiertos)
    }

    const manejarPantallaCompleta = () => {
        setPantallaCompleta(!pantallaCompleta)
    }

    const abrirModalTarea = async (estadoModal: boolean, servicio: ITareaFormulario | null, visualizacion?: boolean) => {
        if (servicio) {
            setEntidadTarea(servicio);
            if (servicio.Id) {
                try {
                    manejarCargando(true, undefined, true);
                    const modulosTarea = await modeloModulosTarea.obtenerListaModulosTarea(servicio.Id!);
                    setEntidadTarea({
                        ...servicio,
                        ModulosChecklist: modulosTarea.Data.map(m => m.IdModuloCatalogo)
                    });

                }
                catch (e) {
                    mostrarNotificacion(
                        EnumTipoNotificacion.Error,
                        'Error al cargar módulos de tarea',
                        'No fue posible cargar los módulos asociados a la tarea. Por favor verifica tu conexión e intenta nuevamente.',
                        5000
                    );
                }
                finally {
                    manejarCargando(false);
                }
            }
        }

        if (visualizacion) {
            setVisualizacion(visualizacion);
        } else setVisualizacion(false);
        setAbrirModal(estadoModal);
    };

    const descargarPlantilla = async () => {
        try {
            manejarCargando(true, undefined, true);
            await modeloTarea.descargarPlantilla();
            mostrarNotificacion(
                EnumTipoNotificacion.Success,
                "Descarga completada",
                "La plantilla de tareas se descargó correctamente.",
                4000
            );
        } catch (error) {
            console.error("Error al descargar la plantilla de tareas:", error);
            mostrarNotificacion(
                EnumTipoNotificacion.Error,
                "Error al descargar",
                "No fue posible descargar la plantilla. Verifica tu conexión e intenta nuevamente.",
                5000
            );
        } finally {
            manejarCargando(false);
        }
    };

    const cargaMasivaTareas = async () => {
        try {
            const input = document.createElement("input");
            input.type = "file";
            input.accept = ".xlsx";

            input.onchange = async (e: any) => {
                const archivo = e.target.files[0];
                if (!archivo) return;

                try {
                    manejarCargando(true, undefined, true);
                    const respuesta = await modeloTarea.cargarMasiva(archivo);

                    if (respuesta.Data) {
                        setDatosRespuestaCarga(respuesta.Data);
                        
                        if (respuesta.Data.Rechazadas && respuesta.Data.Rechazadas.length > 0) {
                            setAbrirModalErrores(true);
                        }
                    }

                    if (respuesta?.Messages?.length > 0) {
                        respuesta.Messages.forEach((mensaje) =>
                            mostrarNotificacion(
                                respuesta.NotificationType ?? EnumTipoNotificacion.Information,
                                "",
                                mensaje,
                                4000
                            )
                        );
                    } else if (respuesta.NotificationType === EnumTipoNotificacion.Success) {
                        mostrarNotificacion(
                            EnumTipoNotificacion.Success,
                            "Carga masiva completada",
                            "El archivo de tareas se cargó correctamente.",
                            4000
                        );
                    }

                    // Recargar tareas
                    const fechaBase = new Date(filtroAgenda.Fecha);
                    const { inicio, fin } = calcularRangoFechas(fechaBase, vista);
                    consultarTareas(inicio, fin, filtroAgenda.Tecnicos);
                } catch (error) {
                    console.error("Error durante la carga masiva de tareas:", error);
                    mostrarNotificacion(
                        EnumTipoNotificacion.Error,
                        "Error al cargar",
                        "No fue posible procesar el archivo. Verifica el formato o la conexión.",
                        5000
                    );
                } finally {
                    manejarCargando(false);
                }
            };

            input.click();
        } catch (error) {
            console.error("Error inicial en carga masiva de equipos:", error);
            mostrarNotificacion(
                EnumTipoNotificacion.Error,
                "Error inesperado",
                "Ocurrió un error al intentar seleccionar el archivo.",
                5000
            );
        }
    };

    const calcularRangoFechas = (fechaBase: Date | string, vistaActual: View) => {
        const d = dayjs(fechaBase);
        let inicio: Date;
        let fin: Date;

        switch (vistaActual) {
            case "month":
                // Envía del 1 al último día del mes
                inicio = d.startOf('month').toDate();
                fin = d.endOf('month').toDate();
                break;
            case "week":
                // Configurado para que la semana empiece en Lunes (ISO)
                inicio = d.startOf('isoWeek').toDate();
                fin = d.endOf('isoWeek').toDate();
                break;
            case "day":
                // Mismo día de 00:00:00 a 23:59:59
                inicio = d.startOf('day').toDate();
                fin = d.endOf('day').toDate();
                break;
            case "agenda":
                // Rango típico de agenda (30 días desde la fecha seleccionada)
                inicio = d.startOf('day').toDate();
                fin = d.add(30, 'day').endOf('day').toDate();
                break;
            default:
                inicio = d.startOf('day').toDate();
                fin = d.endOf('day').toDate();
        }

        return { inicio, fin };
    };

    const refrescarConsulta = () => {
        const { inicio, fin } = calcularRangoFechas(filtroAgenda.Fecha, vista);
        consultarTareas(inicio, fin, filtroAgenda.Tecnicos);
    }

    return (
        <AgendaVista
            abrirModal={abrirModal}
            abrirModalTarea={abrirModalTarea}
            eventosTareas={eventosTareas}
            fecha={fecha}
            manejarCambioFecha={manejarCambioFecha}
            filtrosAbiertos={filtrosAbiertos}
            manejarHabilitarFiltros={manejarHabilitarCambioFiltros}
            vista={vista}
            manejarCambioVista={manejarCambioVista}
            formatosCalendario={formatosCalendario}
            manejarSeleccionEspacio={manejarSeleccionEspacio}
            manejarSeleccionEvento={manejarSeleccionEvento}
            entidadTarea={entidadTarea}
            setEntidadTarea={setEntidadTarea}
            descargarPlantilla={descargarPlantilla}
            listaTecnicos={tecnicosParaAgenda}
            pantallaCompleta={pantallaCompleta}
            manejarCargaMasivaTareas={cargaMasivaTareas}
            manejarPantallaCompleta={manejarPantallaCompleta}
            filtroAgenda={filtroAgenda}
            setFiltroAgenda={setFiltroAgenda}
            visualizacion={visualizacion}
            refrescarConsulta={refrescarConsulta}
            abrirModalErrores={abrirModalErrores}
            setAbrirModalErrores={setAbrirModalErrores}
            datosRespuestaCarga={datosRespuestaCarga}
            entidadesRegistro={entidadesRegistro}
        />
    )
}

export default AgendaCV;